import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AddCustomerForm from "../../components/Customer/AddCustomerForm";
import {
  useFetchCustomerByIdQuery,
  useAdminDeleteCustomerMutation,
  AdminFetchCustomersDocument,
} from "../../generated/graphql";
import { toast } from "react-hot-toast";
import { PopUp } from "../../components/atoms/PopUp";
import BackButton from "@/components/atoms/BackButton";
import Container from "../../components/Container";
import { TableLoading } from "../../components/atoms/TableLoading";
import { Button } from "../../components/ui/Button";
import { Trash2 } from "lucide-react";
import { ConfirmationDialog } from "../../components/ui/ConfirmationDialog";
import { ProtectedRoutes } from "../../types/Routes";

const EditCustomer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState<any>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Fetch customer data by ID
  const { data, loading, error } = useFetchCustomerByIdQuery({
    variables: { customerId: id! },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Delete customer mutation
  const [deleteCustomer, { loading: isDeleting }] =
    useAdminDeleteCustomerMutation({
      onCompleted: data => {
        if (data.data.alert.type === "success") {
          navigate(ProtectedRoutes.CUSTOMERS);
        }
      },
      refetchQueries: [
        {
          query: AdminFetchCustomersDocument,
          variables: {
            params: {
              pagination: {
                page: 1,
                take: 10,
              },
            },
          },
        },
      ],
    });

  useEffect(() => {
    if (data?.data?.payload) {
      const customer = data.data.payload;
      setCustomerData({
        id: customer._id,
        fullName: customer.fullName,
        email: customer.email,
        phone: customer.phone,
        gender: customer.gender,
        address: customer.address,
        country: customer.country,
        avatar: customer.avatar,
        wallet: customer.wallet,
        devices:
          customer.devices?.map((device: any) => ({
            value: device._id,
            label: `${device.serialNumber} - ${
              device.deviceModel?.brand?.brandName || "Unknown Brand"
            } ${device.deviceModel?.modelName || "Unknown Model"}`,
            device: device,
          })) || [],
      });
    }
  }, [data?.data?.payload]);

  const handleDeleteCustomer = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    deleteCustomer({
      variables: {
        customerId: id!,
      },
    });
  };

  if (loading) {
    return (
      <main className="min-h-screen w-full py-[30px]">
        <Container className="space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <BackButton className="" />
              <h1 className="text-textDark font-semibold text-[18px]">
                Edit Customer
              </h1>
            </div>
          </div>
          <TableLoading />
        </Container>
      </main>
    );
  }

  if (error) {
    toast.custom(t => (
      <PopUp t={t} message="Failed to load customer details" type="error" />
    ));
    navigate("/customers");
    return null;
  }

  if (!customerData) {
    return (
      <main className="min-h-screen w-full py-[30px]">
        <Container className="space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <BackButton className="" />
              <h1 className="text-textDark font-semibold text-[18px]">
                Edit Customer
              </h1>
            </div>
          </div>
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-600">Customer not found</p>
          </div>
        </Container>
      </main>
    );
  }

  return (
    <main className="min-h-screen w-full py-[30px]">
      <Container className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            <BackButton className="" />
            <h1 className="text-textDark font-semibold text-[18px]">
              Edit Customer
            </h1>
          </div>
          <Button
            variant="destructive"
            onClick={handleDeleteCustomer}
            className="flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete Customer
          </Button>
        </div>
        {customerData && (
          <AddCustomerForm
            isEditMode={true}
            initialData={customerData}
            customerId={id}
          />
        )}
      </Container>

      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDelete}
        title="Delete Customer"
        description="Are you sure you want to delete this customer? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant="destructive"
      />
    </main>
  );
};

export default EditCustomer;
